import { useEffect, useState, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Chip, Avatar, Stack, Divider, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControlLabel, Checkbox, Alert, CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Security as SecurityIcon, Lock as LockIcon, People as PeopleIcon,
  Article as PageIcon, Verified as VerifiedIcon,
  Assessment as ReportsIcon, GavelOutlined as ApprovalIcon,
  Close as CloseIcon, Tag as TagIcon, Label as LabelIcon,
  Description as DescIcon,
} from '@mui/icons-material';
import { InputAdornment } from '@mui/material';
import API from '../../BaseURL';
import swal from '../../utils/swal';

function StatTile({ icon, label, value, tone = 'primary', sub }) {
  return (
    <Card sx={{
      flex: 1, minWidth: 180, borderRadius: 2,
      borderLeft: (t) => `4px solid ${t.palette[tone].main}`,
      transition: 'transform .15s, box-shadow .15s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
    }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.8, py: 1.8 }}>
        <Box sx={{
          width: 46, height: 46, borderRadius: '50%',
          bgcolor: (t) => alpha(t.palette[tone].main, 0.14),
          color: `${tone}.main`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{
            textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 800, fontSize: 10, display: 'block',
          }}>{label}</Typography>
          <Typography variant="h5" fontWeight={800} lineHeight={1.15}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary" noWrap>{sub}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function RolesPage() {
  const [roles, setRoles]       = useState([]);
  const [pages, setPages]       = useState([]);
  const [reports, setReports]   = useState([]);
  const [approvalsReg, setApprovalsReg] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [openDlg, setOpenDlg]   = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState({ name: '', label: '', description: '', pages: [], reports: [], approvals: [], is_teacher_role: false });
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [dlgTab, setDlgTab]     = useState('pages');

  const load = async () => {
    setLoading(true);
    try {
      const [rolesRes, pagesRes, reportsRes, approvalsRes] = await Promise.all([
        API.get('/roles'),
        API.get('/roles/pages'),
        API.get('/roles/reports'),
        API.get('/roles/approvals'),
      ]);
      setRoles(rolesRes.data); setPages(pagesRes.data);
      setReports(reportsRes.data); setApprovalsReg(approvalsRes.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', label: '', description: '', pages: [], reports: [], approvals: [], is_teacher_role: false });
    setError(''); setDlgTab('pages'); setOpenDlg(true);
  };

  const openEdit = async (role) => {
    setError('');
    try {
      const res = await API.get(`/roles/${role.id}`);
      setEditing(role);
      setForm({
        name: res.data.name, label: res.data.label,
        description: res.data.description || '',
        pages:     res.data.pages     || [],
        reports:   res.data.reports   || [],
        approvals: res.data.approvals || [],
        is_teacher_role: !!res.data.is_teacher_role,
      });
      setDlgTab('pages'); setOpenDlg(true);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load role');
    }
  };

  const togglePage = (key) => {
    setForm((f) => ({
      ...f,
      pages: f.pages.includes(key) ? f.pages.filter(p => p !== key) : [...f.pages, key],
    }));
  };

  const toggleReport = (key) => {
    setForm((f) => ({
      ...f,
      reports: f.reports.includes(key) ? f.reports.filter(r => r !== key) : [...f.reports, key],
    }));
  };

  const toggleApproval = (key) => {
    setForm((f) => ({
      ...f,
      approvals: f.approvals.includes(key) ? f.approvals.filter(a => a !== key) : [...f.approvals, key],
    }));
  };

  const toggleApprovalSection = (sectionApprovals) => {
    const allKeys = sectionApprovals.map(a => a.key);
    const allChecked = allKeys.every(k => form.approvals.includes(k));
    setForm((f) => ({
      ...f,
      approvals: allChecked
        ? f.approvals.filter(a => !allKeys.includes(a))
        : Array.from(new Set([...f.approvals, ...allKeys])),
    }));
  };

  const toggleSection = (sectionPages) => {
    const allKeys = sectionPages.map(p => p.key);
    const allChecked = allKeys.every(k => form.pages.includes(k));
    setForm((f) => ({
      ...f,
      pages: allChecked
        ? f.pages.filter(p => !allKeys.includes(p))
        : Array.from(new Set([...f.pages, ...allKeys])),
    }));
  };

  const toggleReportCategory = (categoryReports) => {
    const allKeys = categoryReports.map(r => r.key);
    const allChecked = allKeys.every(k => form.reports.includes(k));
    setForm((f) => ({
      ...f,
      reports: allChecked
        ? f.reports.filter(r => !allKeys.includes(r))
        : Array.from(new Set([...f.reports, ...allKeys])),
    }));
  };

  const submit = async () => {
    if (!form.label.trim()) { setError('Label is required'); return; }
    if (!editing && !form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) {
        await API.put(`/roles/${editing.id}`, {
          label: form.label, description: form.description,
          pages: form.pages, reports: form.reports, approvals: form.approvals,
          is_teacher_role: form.is_teacher_role,
        });
        setOpenDlg(false);
        swal.success(`Role "${form.label}" updated`);
      } else {
        const created = form.label;
        await API.post('/roles', form);
        setForm({ name: '', label: '', description: '', pages: [], reports: [], approvals: [], is_teacher_role: false });
        swal.success(`Role "${created}" created — add another or close`);
      }
      await load();
    } catch (e) {
      setError(swal.pickError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const remove = async (role) => {
    const ok = await swal.confirm({
      title: `Delete role "${role.label}"?`,
      text: 'Users currently assigned to this role will lose all access granted by it. This cannot be undone.',
      confirmText: 'Yes, delete role',
      cancelText: 'Keep it',
      icon: 'warning',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await API.delete(`/roles/${role.id}`);
      swal.success('Role deleted');
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  const grouped = useMemo(() => {
    const map = {};
    pages.forEach(p => {
      map[p.section] = map[p.section] || [];
      map[p.section].push(p);
    });
    return map;
  }, [pages]);

  const groupedReports = useMemo(() => {
    const map = {};
    reports.forEach(r => {
      map[r.category] = map[r.category] || [];
      map[r.category].push(r);
    });
    return map;
  }, [reports]);

  const groupedApprovals = useMemo(() => {
    const map = {};
    approvalsReg.forEach(a => {
      map[a.section] = map[a.section] || [];
      map[a.section].push(a);
    });
    return map;
  }, [approvalsReg]);

  const stats = useMemo(() => ({
    total:    roles.length,
    system:   roles.filter(r => r.is_system === 1).length,
    custom:   roles.filter(r => r.is_system !== 1).length,
    assigned: roles.reduce((s, r) => s + Number(r.user_count || 0), 0),
  }), [roles]);

  return (
    <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
      {/* Header */}
      <Card sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff', p: { xs: 2.5, md: 3 },
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 2,
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 52, height: 52 }}>
              <SecurityIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={800}>Roles & Permissions</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Define roles and choose which pages users with each role can see
              </Typography>
            </Box>
          </Stack>
          <Button variant="contained" color="inherit"
            startIcon={<AddIcon />} onClick={openCreate}
            sx={{ bgcolor: '#fff', color: 'primary.main', '&:hover': { bgcolor: '#f0f0f0' } }}>
            New Role
          </Button>
        </Box>
      </Card>

      {/* Stats */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <StatTile icon={<SecurityIcon />} label="Total Roles" value={stats.total}    tone="primary"
          sub={`${stats.system} system · ${stats.custom} custom`} />
        <StatTile icon={<VerifiedIcon />} label="System"      value={stats.system}   tone="secondary"
          sub="Built-in, locked" />
        <StatTile icon={<EditIcon />}     label="Custom"      value={stats.custom}   tone="success"
          sub="Editable by admins" />
        <StatTile icon={<PeopleIcon />}   label="Users Assigned" value={stats.assigned} tone="warning"
          sub={stats.assigned ? 'Across all roles' : 'No users assigned yet'} />
      </Stack>

      {error && !openDlg && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }} onClose={() => setError('')}>{error}</Alert>
      )}

      {/* Table */}
      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
          <SecurityIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={800}>Defined Roles</Typography>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" variant="outlined"
            label={`${roles.length} role${roles.length === 1 ? '' : 's'}`}
            sx={{ fontWeight: 700 }} />
        </Box>

        {loading ? (
          <Box sx={{ p: 8, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table sx={{
              '& .MuiTableCell-head': {
                bgcolor: 'grey.50',
                fontSize: 11,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'text.secondary',
                borderBottom: 2,
                borderColor: 'divider',
              },
              '& .MuiTableRow-root:not(:last-child) .MuiTableCell-body': {
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
              '& .MuiTableRow-hover:hover': {
                bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
              },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Users</TableCell>
                  <TableCell align="center">Pages</TableCell>
                  <TableCell align="center">Reports</TableCell>
                  <TableCell align="center">Approvals</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map(r => {
                  const isSuper = r.name === 'super_admin';
                  const isSystem = r.is_system === 1;
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ py: 1.4 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{
                            width: 40, height: 40, fontSize: 15, fontWeight: 700,
                            bgcolor: isSuper ? 'secondary.main' : (isSystem ? 'primary.dark' : 'primary.main'),
                            boxShadow: isSuper ? (t) => `0 0 0 3px ${alpha(t.palette.secondary.main, 0.2)}` : 'none',
                          }}>
                            {r.label?.[0]?.toUpperCase()}
                          </Avatar>
                          <Box>
                            <Stack direction="row" spacing={0.6} alignItems="center">
                              <Typography variant="body2" fontWeight={700}>{r.label}</Typography>
                              {isSystem && (
                                <Tooltip title="System role — cannot be deleted">
                                  <LockIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                                </Tooltip>
                              )}
                              {isSuper && (
                                <Chip size="small" color="secondary" label="Super"
                                  sx={{ height: 18, fontWeight: 800, '& .MuiChip-label': { px: 0.7, fontSize: 10 } }} />
                              )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary"
                              sx={{ fontFamily: 'monospace' }}>
                              {r.name}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2"
                          color={r.description ? 'text.primary' : 'text.disabled'}
                          sx={{ fontStyle: r.description ? 'normal' : 'italic' }}>
                          {r.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" icon={<PeopleIcon sx={{ fontSize: 13 }} />}
                          label={r.user_count || 0}
                          variant={Number(r.user_count) > 0 ? 'filled' : 'outlined'}
                          color={Number(r.user_count) > 0 ? 'default' : 'default'}
                          sx={{ fontWeight: 700, height: 24, minWidth: 56 }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" icon={<PageIcon sx={{ fontSize: 13 }} />}
                          label={isSuper ? 'All' : (r.page_count || 0)}
                          color="primary" variant={isSuper ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 700, height: 24, minWidth: 56 }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" icon={<ReportsIcon sx={{ fontSize: 13 }} />}
                          label={isSuper ? 'All' : (r.report_count || 0)}
                          color="secondary" variant={isSuper ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 700, height: 24, minWidth: 56 }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" icon={<ApprovalIcon sx={{ fontSize: 13 }} />}
                          label={isSuper ? 'All' : (r.approval_count || 0)}
                          color="warning" variant={isSuper ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 700, height: 24, minWidth: 56 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.3} justifyContent="flex-end">
                          <Tooltip title={isSuper ? 'Super admin always has full access' : 'Edit role'}>
                            <span>
                              <IconButton size="small" color="primary" onClick={() => openEdit(r)}
                                disabled={isSuper}
                                sx={{ '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.12) } }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={isSystem ? 'System roles cannot be deleted' : 'Delete role'}>
                            <span>
                              <IconButton size="small" color="error" onClick={() => remove(r)}
                                disabled={isSystem}
                                sx={{ '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.12) } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {roles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <SecurityIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.disabled" fontWeight={700}>No roles yet</Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                        Click "New Role" above to define your first role.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={openDlg} onClose={() => setOpenDlg(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        {/* Header */}
        <Box sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff', px: 3, py: 2.2,
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 46, height: 46, color: '#fff' }}>
            {editing ? (form.label?.[0]?.toUpperCase() || <SecurityIcon />) : <AddIcon />}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
              {editing ? editing.label : 'Create New Role'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {editing
                ? 'Update label, description, and the permissions granted to this role'
                : 'Define a new permission set you can assign to users'}
            </Typography>
          </Box>
          <IconButton onClick={() => setOpenDlg(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 0 }}>
          {error && (
            <Box sx={{ p: 2, pb: 0 }}>
              <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 1.5 }}>{error}</Alert>
            </Box>
          )}

          {/* Role Information */}
          <Box sx={{ p: 2 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <Box sx={{ px: 2.2, py: 1.2, borderBottom: 1, borderColor: 'divider',
                display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                <SecurityIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={800}>Role Information</Typography>
              </Box>
              <Box sx={{
                p: 2.2, display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 2fr' },
                gap: 1.8,
              }}>
                <TextField size="small" label="Role Name (slug)" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  disabled={!!editing}
                  helperText={editing ? 'Cannot be renamed' : 'lowercase, no spaces — e.g. accountant'}
                  InputProps={{ startAdornment: (
                    <InputAdornment position="start"><TagIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>
                  ) }} />
                <TextField size="small" label="Display Label" value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="e.g. Accountant" required
                  helperText="Shown to users"
                  InputProps={{ startAdornment: (
                    <InputAdornment position="start"><LabelIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>
                  ) }} />
                <TextField size="small" label="Description (optional)"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  helperText="Briefly describe what this role does"
                  InputProps={{ startAdornment: (
                    <InputAdornment position="start"><DescIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>
                  ) }} />
              </Box>
              {/* Teacher-role flag. Surfaces this role to the
                  Examination → Teacher Assignments panel. */}
              <Box sx={{ px: 2.2, pb: 1.8, mt: -0.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox size="small"
                      checked={!!form.is_teacher_role}
                      onChange={(e) => setForm({ ...form, is_teacher_role: e.target.checked })} />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                        This role represents teachers
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tick this so users on this role show up in <b>Examination Settings → Teacher Assignments</b>
                        for class &amp; subject ownership.
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: 'flex-start' }}
                />
              </Box>
            </Card>
          </Box>

          {/* Sticky tab strip */}
          <Box sx={{
            position: 'sticky', top: 0, zIndex: 2,
            bgcolor: 'background.paper',
            borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
            px: 2,
          }}>
            <Tabs value={dlgTab} onChange={(_, v) => setDlgTab(v)}
              sx={{
                minHeight: 48,
                '& .MuiTab-root': { minHeight: 48, fontWeight: 700, textTransform: 'none', px: 2 },
              }}>
              <Tab value="pages" label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <PageIcon fontSize="small" />
                  <span>Page Access</span>
                  <Chip size="small" label={form.pages.length}
                    color={form.pages.length ? 'primary' : 'default'}
                    sx={{ fontWeight: 800, height: 20, '& .MuiChip-label': { px: 0.8 } }} />
                </Stack>
              } />
              <Tab value="reports" label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <ReportsIcon fontSize="small" />
                  <span>Reports Access</span>
                  <Chip size="small" label={form.reports.length}
                    color={form.reports.length ? 'secondary' : 'default'}
                    sx={{ fontWeight: 800, height: 20, '& .MuiChip-label': { px: 0.8 } }} />
                </Stack>
              } />
              <Tab value="approvals" label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <ApprovalIcon fontSize="small" />
                  <span>Approver Rights</span>
                  <Chip size="small" label={form.approvals.length}
                    color={form.approvals.length ? 'warning' : 'default'}
                    sx={{ fontWeight: 800, height: 20, '& .MuiChip-label': { px: 0.8 } }} />
                </Stack>
              } />
            </Tabs>
          </Box>

          {/* Tab body */}
          <Box sx={{ p: 2 }}>
            {dlgTab === 'pages' && (
              <>
                <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 1.5 }}>
                  Tick the pages this role can see in their sidebar. Use <strong>Select all</strong> per section to grant a whole area at once.
                </Alert>
                {Object.entries(grouped).map(([section, items]) => {
                  const allChecked = items.every(i => form.pages.includes(i.key));
                  const someChecked = items.some(i => form.pages.includes(i.key));
                  const onCount = items.filter(i => form.pages.includes(i.key)).length;
                  return (
                    <Card key={section} variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
                      <Box sx={{
                        px: 2.2, py: 1.2, bgcolor: 'background.paper',
                        display: 'flex', alignItems: 'center', gap: 1,
                        borderBottom: '1px solid', borderColor: 'divider',
                      }}>
                        <Typography variant="subtitle2" fontWeight={800}>{section}</Typography>
                        {onCount > 0 && (
                          <Chip size="small" color="primary" label={`${onCount}/${items.length}`}
                            sx={{ fontWeight: 700, height: 22 }} />
                        )}
                        <Box sx={{ flex: 1 }} />
                        <FormControlLabel
                          control={<Checkbox size="small" checked={allChecked}
                            indeterminate={someChecked && !allChecked}
                            onChange={() => toggleSection(items)} />}
                          label={<Typography variant="caption" fontWeight={700}>Select all</Typography>}
                          sx={{ m: 0 }} />
                      </Box>
                      <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                        {items.map(p => (
                          <FormControlLabel key={p.key}
                            sx={{ m: 0, px: 1, py: 0.4, borderRadius: 1,
                              '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.05) } }}
                            control={<Checkbox size="small" checked={form.pages.includes(p.key)} onChange={() => togglePage(p.key)} />}
                            label={<Typography variant="body2">{p.label}</Typography>} />
                        ))}
                      </Box>
                    </Card>
                  );
                })}
              </>
            )}

            {dlgTab === 'reports' && (
              <>
                <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 1.5 }}>
                  Tick the individual reports users with this role can <strong>run, print and export</strong>.
                </Alert>
                {Object.entries(groupedReports).map(([category, items]) => {
                  const allChecked = items.every(i => form.reports.includes(i.key));
                  const someChecked = items.some(i => form.reports.includes(i.key));
                  const onCount = items.filter(i => form.reports.includes(i.key)).length;
                  return (
                    <Card key={category} variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
                      <Box sx={{
                        px: 2.2, py: 1.2, bgcolor: 'background.paper',
                        display: 'flex', alignItems: 'center', gap: 1,
                        borderBottom: '1px solid', borderColor: 'divider',
                      }}>
                        <Typography variant="subtitle2" fontWeight={800}>{category}</Typography>
                        {onCount > 0 && (
                          <Chip size="small" color="secondary" label={`${onCount}/${items.length}`}
                            sx={{ fontWeight: 700, height: 22 }} />
                        )}
                        <Box sx={{ flex: 1 }} />
                        <FormControlLabel
                          control={<Checkbox size="small" checked={allChecked}
                            indeterminate={someChecked && !allChecked}
                            onChange={() => toggleReportCategory(items)} />}
                          label={<Typography variant="caption" fontWeight={700}>Select all</Typography>}
                          sx={{ m: 0 }} />
                      </Box>
                      <Box sx={{ p: 1.5, display: 'grid', gap: 0.4 }}>
                        {items.map(r => (
                          <FormControlLabel key={r.key}
                            sx={{ m: 0, px: 1, py: 0.6, borderRadius: 1, alignItems: 'flex-start',
                              '&:hover': { bgcolor: (t) => alpha(t.palette.secondary.main, 0.05) } }}
                            control={<Checkbox size="small" checked={form.reports.includes(r.key)} onChange={() => toggleReport(r.key)}
                              sx={{ mt: -0.5 }} />}
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={700}>{r.label}</Typography>
                                {r.desc && <Typography variant="caption" color="text.secondary">{r.desc}</Typography>}
                              </Box>
                            } />
                        ))}
                      </Box>
                    </Card>
                  );
                })}
                {Object.keys(groupedReports).length === 0 && (
                  <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ py: 4 }}>
                    No reports defined yet.
                  </Typography>
                )}
              </>
            )}

            {dlgTab === 'approvals' && (
              <>
                <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderRadius: 1.5 }} icon={<ApprovalIcon fontSize="small" />}>
                  Tick the sensitive actions users with this role can approve.
                  <strong> Super admin always passes</strong> regardless of these grants.
                </Alert>
                {Object.entries(groupedApprovals).map(([section, items]) => {
                  const allChecked  = items.every(i => form.approvals.includes(i.key));
                  const someChecked = items.some(i => form.approvals.includes(i.key));
                  const onCount = items.filter(i => form.approvals.includes(i.key)).length;
                  return (
                    <Card key={section} variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
                      <Box sx={{
                        px: 2.2, py: 1.2, bgcolor: 'background.paper',
                        display: 'flex', alignItems: 'center', gap: 1,
                        borderBottom: '1px solid', borderColor: 'divider',
                      }}>
                        <Typography variant="subtitle2" fontWeight={800}>{section}</Typography>
                        {onCount > 0 && (
                          <Chip size="small" color="warning" label={`${onCount}/${items.length}`}
                            sx={{ fontWeight: 700, height: 22 }} />
                        )}
                        <Box sx={{ flex: 1 }} />
                        <FormControlLabel
                          control={<Checkbox size="small" checked={allChecked}
                            indeterminate={someChecked && !allChecked}
                            onChange={() => toggleApprovalSection(items)} />}
                          label={<Typography variant="caption" fontWeight={700}>Select all</Typography>}
                          sx={{ m: 0 }} />
                      </Box>
                      <Box sx={{ p: 1.5, display: 'grid', gap: 0.4 }}>
                        {items.map(a => (
                          <FormControlLabel key={a.key}
                            sx={{ m: 0, px: 1, py: 0.6, borderRadius: 1, alignItems: 'flex-start',
                              '&:hover': { bgcolor: (t) => alpha(t.palette.warning.main, 0.06) } }}
                            control={<Checkbox size="small"
                              checked={form.approvals.includes(a.key)}
                              onChange={() => toggleApproval(a.key)}
                              sx={{ mt: -0.5 }} />}
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={700}>{a.label}</Typography>
                                {a.desc && <Typography variant="caption" color="text.secondary">{a.desc}</Typography>}
                              </Box>
                            } />
                        ))}
                      </Box>
                    </Card>
                  );
                })}
                {Object.keys(groupedApprovals).length === 0 && (
                  <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ py: 4 }}>
                    No approval rights defined yet.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.7, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ flex: 1, color: 'text.secondary', fontSize: 12 }}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Chip size="small" icon={<PageIcon sx={{ fontSize: 13 }} />} label={`${form.pages.length} pages`}
                color={form.pages.length ? 'primary' : 'default'} variant={form.pages.length ? 'filled' : 'outlined'}
                sx={{ fontWeight: 700, height: 22 }} />
              <Chip size="small" icon={<ReportsIcon sx={{ fontSize: 13 }} />} label={`${form.reports.length} reports`}
                color={form.reports.length ? 'secondary' : 'default'} variant={form.reports.length ? 'filled' : 'outlined'}
                sx={{ fontWeight: 700, height: 22 }} />
              <Chip size="small" icon={<ApprovalIcon sx={{ fontSize: 13 }} />} label={`${form.approvals.length} approvals`}
                color={form.approvals.length ? 'warning' : 'default'} variant={form.approvals.length ? 'filled' : 'outlined'}
                sx={{ fontWeight: 700, height: 22 }} />
            </Stack>
          </Box>
          <Button onClick={() => setOpenDlg(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : (editing ? <EditIcon /> : <AddIcon />)}
            sx={{ minWidth: 150, fontWeight: 700 }}>
            {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create Role')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
